import { ActionIcon, Box, Button, Divider, Group, Image, Loader, Paper, Stack, Switch, Text, TextInput, Tooltip } from "@mantine/core";
import { useHover } from "@mantine/hooks";
import ReactPlayer from 'react-player'
import { IconEdit, IconHistory, IconInfoCircle, IconMarkdown, IconReload, IconTool } from "@tabler/icons-react";
import { ChatModel } from "./ChatResultModel";
import { useState } from "react";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { EmbeddingContextDataPage } from "./EmbeddingContextDataPage";
import { SumTokenUsageDataWithModel } from "@/Helpers/TokenUsageHelper";
import { AutoLink } from "@/Components/AutoLink";
import { MarkdownMessage } from "@/Components/MarkdownMessage";

export function ChatRequestCard(props: { workspaceId: string, chatModel: ChatModel, onReload?: () => void, onEditText?: (v: string) => void }) {
    const { hovered: request_hovered, ref: request_ref } = useHover();
    const [editing, setEditing] = useState(false)
    const [enableMarkdown, setEnableMarkdown] = useState(true)
    const form = useForm({
        initialValues: {
            editText: '',
        }
    });
    const tokenUsage = SumTokenUsageDataWithModel(props.chatModel.response.tokenUsage ?? [])
    return <>
        {props.chatModel.requestText !== undefined &&
            <Box ref={request_ref} pos="relative" display="inline-flex" pb={20} style={{ justifyContent: "end" }} >
                {editing ? <>
                    <Paper w="100%" radius="lg" p="lg">
                        <form onSubmit={form.onSubmit((values) => {
                            props.onEditText?.(values.editText)
                            setEditing(false)
                        })}>
                            <TextInput
                                variant="unstyled"
                                placeholder="修改訊息"
                                radius="md"
                                {...form.getInputProps('editText')}
                            />
                            <Group gap={5} justify="end">
                                <Button radius="xl" color="gray" onClick={() => setEditing(false)}>取消</Button>
                                <Button radius="xl" type="submit">傳送</Button>
                            </Group>
                        </form>
                    </Paper>
                </> :
                    <>
                        <Paper p="md" radius="xl" bg="var(--mantine-primary-color-light)" >
                            {enableMarkdown ? (
                                <Box style={{ fontSize: 'var(--mantine-font-size-sm)' }}>
                                    <MarkdownMessage>{props.chatModel.requestText}</MarkdownMessage>
                                </Box>
                            ) : (
                                <Text size="sm">{props.chatModel.requestText}</Text>
                            )}
                        </Paper>
                        <Group gap={0} display={request_hovered && !props.chatModel.responseLoading ? "block" : "none"} pos="absolute" bottom={-5} >
                            <ActionIcon variant="transparent" onClick={() => {
                                form.setFieldValue("editText", props.chatModel.requestText ?? "")
                                setEditing(true)
                            }}>
                                <IconEdit style={{ width: '70%', height: '70%' }} stroke={1.5} />
                            </ActionIcon>
                            <ActionIcon variant="transparent" onClick={props.onReload}>
                                <IconReload style={{ width: '70%', height: '70%' }} stroke={1.5} />
                            </ActionIcon>
                        </Group>
                    </>
                }
            </Box>
        }
        <Box pos="relative" display="inline-flex">
            <Paper p="md" radius="xl">
                <Group gap={0}>
                    <Tooltip color="gray" multiline events={{ hover: true, focus: false, touch: true }} style={{ whiteSpace: "pre-line" }} label={`Model Id:${props.chatModel.response.modelId}\nInput Tokens:${tokenUsage.inputTokens ?? 0}\nOutput Tokens:${tokenUsage.outputTokens ?? 0}\n回應時間:${props.chatModel.responseTime ?? 0}秒`}>
                        <Group gap={0}>
                            <Text c="gray" size="sm">AI Bot</Text>
                        </Group>
                    </Tooltip>
                    {!props.chatModel.responseLoading &&
                        <>
                            <Tooltip color="gray" label="聊天歷史">
                                <ActionIcon variant="transparent" onClick={() => {
                                    modals.open({
                                        title: '聊天歷史',
                                        fullScreen: true,
                                        children: (
                                            <Text style={{ whiteSpace: "pre-wrap" }}>
                                                {props.chatModel.response.chatHistoryJson}
                                            </Text>
                                        ),
                                    });
                                }}>
                                    <IconHistory style={{ width: '70%', height: '70%' }} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                            {(props.chatModel.response.contexts?.length ?? 0) > 0 &&
                                <Tooltip color="gray" label="Context資訊">
                                    <ActionIcon variant="transparent" onClick={() => {
                                        modals.open({
                                            title: 'Context資訊',
                                            fullScreen: true,
                                            children: (
                                                <Box>
                                                    {props.chatModel.response.contexts?.map((context, idx) => <EmbeddingContextDataPage key={`Embedding_${idx}`} data={context} workspaceId={props.workspaceId} />)}
                                                </Box>
                                            ),
                                        });
                                    }}>
                                        <IconInfoCircle style={{ width: '70%', height: '70%' }} stroke={1.5} />
                                    </ActionIcon>
                                </Tooltip>
                            }
                            {(props.chatModel.response.newToolRecords?.length ?? 0) > 0 &&
                                <Tooltip color="gray" label="工具調用">
                                    <ActionIcon variant="transparent" onClick={() => {
                                        modals.open({
                                            title: '工具調用',
                                            fullScreen: true,
                                            children: (
                                                <Stack>
                                                    {props.chatModel.response.newToolRecords?.map((x, idx) => <>
                                                        <Stack gap={5}>
                                                            <Text>
                                                                工具:{x.pluginName}.{x.functionName}
                                                            </Text>
                                                            <Text>
                                                                參數:{x.arguments}
                                                            </Text>
                                                            <Text style={{ whiteSpace: "pre-wrap" }}>
                                                                結果:
                                                                {x.message}
                                                            </Text>
                                                        </Stack>
                                                        <Divider />
                                                    </>)}
                                                </Stack>
                                            ),
                                        });
                                    }}>
                                        <IconTool style={{ width: '70%', height: '70%' }} stroke={1.5} />
                                    </ActionIcon>
                                </Tooltip>
                            }
                            <Tooltip color="gray" label="Markdown顯示">
                                <Switch size="sm" onLabel={<IconMarkdown size={16} />} offLabel={<IconMarkdown size={16} />} checked={enableMarkdown} onChange={(e) => setEnableMarkdown(e.currentTarget.checked)} ml="5px" />
                            </Tooltip>
                        </>
                    }
                </Group>
                {props.chatModel.responseFail ? <Text size="sm" c="red" style={{ whiteSpace: "pre-wrap" }}>
                    發生錯誤:
                    <br />
                    {props.chatModel.responseFailMsg}
                </Text> : props.chatModel.responseLoading ? <Loader size="sm" type="dots" /> :
                    <>
                        {/* <Text size="sm" style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>{props.chatModel.response.text}</Text> */}
                        {enableMarkdown ? (
                            <Box style={{ fontSize: 'var(--mantine-font-size-sm)' }}>
                                <MarkdownMessage>
                                    {props.chatModel.response.text}
                                </MarkdownMessage>
                            </Box>
                        ) : (
                            <AutoLink textProps={{
                                size: "sm"
                            }} >
                                {props.chatModel.response.text}
                            </AutoLink>
                        )}
                        {props.chatModel.response.mediaUrls && props.chatModel.response.mediaUrls.map((x, idx) => <>
                            {x.type == "image" &&
                                <Image key={`image_${idx}`} src={x.url} maw="100%" />
                            }
                            {x.type == "video" &&
                                <ReactPlayer key={`video${idx}`} controls={true} src={x.url} width="100%" />
                            }
                        </>)}
                    </>
                }
            </Paper>
        </Box>
    </>

}